import {
  Badge,
  Banner,
  Box,
  Divider,
  DropdownButtonContext,
  FlexBox,
  IconButton,
  InputSelectionList,
  InputSelectionListChangeEvent,
  SelectionList,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { ampli } from 'lib-shared-tracking';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AiLoadingState } from 'components/aiLoadingState';
import { useCopilotPreconditions } from 'components/copilotActions';
import { CopilotDropdownActionContext } from 'components/copilotDropdown';
import { HtmlRenderer } from 'components/htmlRenderer';
import { getMimDraftFailureMessage } from 'utils/mimDraft';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useDraftAiAgents } from 'utils/useDraftAiAgents';
import { sanitizationProfile, useEditorContext } from 'utils/useEditorContext';
import { useCurrentUser } from 'utils/user/useCurrentUser';

import {
  CopilotDropdownFooter,
  CopilotDropdownHeader,
} from '../../../copilotDropdown/components';
import { useRunAiAgent } from '../../hooks/useRunAiAgent';
import { EditorCopilotDropdownContentBox } from '../contentBox';

import styles from './editorCopilotDropdownCustomAiDraft.module.scss';

const additionalFeedback = {
  feature: 'ai_draft',
};

interface CustomAiDraftContentProps {
  dixaConversationId: string;
}

export const CustomAiDraftContent = memo(function CustomAiDraftContent({
  dixaConversationId,
}: CustomAiDraftContentProps) {
  const { user } = useCurrentUser();
  const {
    agents,
    defaultAgentId,
    userDefaultAgentId,
    orgDefaultAgentId,
    setUserDefaultAgentId,
    isLoading: isLoadingAgents,
    isError: isAgentsError,
  } = useDraftAiAgents();
  const noAgents = !isLoadingAgents && agents.length === 0;

  const organizationName = useAppSelector(
    (state) => state.settings.organization.organization.name,
  );
  const orgDefaultLabel = organizationName
    ? `${organizationName} default`
    : 'Organization default';

  const { onBack } = useContext(CopilotDropdownActionContext);
  const { insertBelow, replace } = useEditorContext();
  const dropdownContext = useContext(DropdownButtonContext);
  const csid = Number(dixaConversationId);

  const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>(
    undefined,
  );
  // Uses the preference id while the agents are still loading.
  const preferredAgentId = selectedAgentId ?? defaultAgentId;
  const selectedAgent = isLoadingAgents
    ? undefined
    : (agents.find((a) => a.id === preferredAgentId) ?? agents[0]);
  const activeAgentId = isLoadingAgents ? preferredAgentId : selectedAgent?.id;
  const isDefaultAgent =
    !!selectedAgent && selectedAgent.id === userDefaultAgentId;

  const handleSaveDefaultAgent = useCallback(() => {
    if (!selectedAgent) {
      return;
    }

    setUserDefaultAgentId(selectedAgent.id);
  }, [selectedAgent, setUserDefaultAgentId]);

  const { runAgent, text, isLoading, isError, failureReason } = useRunAiAgent(
    activeAgentId ?? '',
    dixaConversationId,
    user.id,
  );

  const { hasUnsupportedAttachment } = useCopilotPreconditions();

  // Drafts when the active agent becomes known or changes.
  useEffect(() => {
    if (!activeAgentId || hasUnsupportedAttachment) {
      return;
    }

    runAgent();
  }, [activeAgentId, hasUnsupportedAttachment, runAgent]);

  const [agentSearchTerm, setAgentSearchTerm] = useState('');

  const filteredAgents = useMemo(() => {
    const searchTerm = agentSearchTerm.trim();
    if (!searchTerm) {
      return agents;
    }

    const fuse = new Fuse(agents, {
      keys: ['name'],
      threshold: 0.2,
      distance: 200,
    });
    return fuse.search(searchTerm).map(({ item }) => item);
  }, [agentSearchTerm, agents]);

  const handleAgentSearchChange = useCallback((event) => {
    setAgentSearchTerm(event.target.value);
  }, []);

  const handleAgentChange = useCallback(
    (event: InputSelectionListChangeEvent<string>) => {
      const agentId = event.selectedItem?.value;
      if (!agentId) return;
      setAgentSearchTerm('');
      setSelectedAgentId(agentId);
    },
    [],
  );

  const canUseDraft = !!text && !isLoading;

  const content = useMemo(() => {
    if (hasUnsupportedAttachment) {
      return (
        <Banner
          variant="fail"
          description={
            <span>{getMimDraftFailureMessage('unsupported_message')}</span>
          }
        />
      );
    }
    if (isAgentsError && !activeAgentId) {
      return (
        <Banner
          variant="fail"
          description={<span>Could not load agents. Please try again.</span>}
        />
      );
    }
    if (noAgents) {
      return (
        <Banner
          variant="info"
          description={<span>No agents configured.</span>}
        />
      );
    }
    if (text) {
      return (
        <HtmlRenderer
          sanitizationProfileType={sanitizationProfile}
          html={text}
        />
      );
    }
    if (isLoading) {
      return <AiLoadingState label="" />;
    }
    if (isError) {
      return (
        <Banner
          variant="fail"
          description={
            <span>
              {failureReason
                ? getMimDraftFailureMessage(failureReason)
                : 'An error occurred while generating the reply.'}
            </span>
          }
        />
      );
    }
    return (
      <Text.Paragraph color="tertiary">
        Select an AI agent to draft a reply based on this conversation.
      </Text.Paragraph>
    );
  }, [
    activeAgentId,
    failureReason,
    hasUnsupportedAttachment,
    isAgentsError,
    noAgents,
    text,
    isLoading,
    isError,
  ]);

  const handleInsertBelow = useCallback(() => {
    if (!text) {
      return;
    }

    insertBelow(text);
    dropdownContext.closeDropdown();

    ampli.agentCopilotTextInsertBelowClicked({ key: 'customAiDraft', csid });
  }, [text, insertBelow, dropdownContext, csid]);

  const handleReplace = useCallback(() => {
    if (!text) {
      return;
    }

    replace(text);
    dropdownContext.closeDropdown();

    ampli.agentCopilotTextReplaceClicked({ key: 'customAiDraft', csid });
  }, [text, replace, dropdownContext, csid]);

  const agentPicker = noAgents ? null : (
    <InputSelectionList<string>
      value={selectedAgent ? [selectedAgent.id] : []}
      getItemTitleByValue={(id) =>
        agents.find((a) => a.id === id)?.name ?? id ?? ''
      }
      placeholder={isLoadingAgents ? 'Loading agents...' : 'Select an AI agent'}
      filter={{
        value: agentSearchTerm,
        onChange: handleAgentSearchChange,
      }}
      onChange={handleAgentChange}
      style={{ maxWidth: 320 }}
      disabled={isLoadingAgents}
    >
      {filteredAgents.map((agent) => (
        <SelectionList.Item key={agent.id} value={agent.id}>
          <FlexBox gap="200" alignItems="center">
            <span className={styles.agentName}>{agent.name ?? agent.id}</span>

            {agent.id === userDefaultAgentId && (
              <Badge className={styles.agentBadge} size="small" variant="info">
                Your default
              </Badge>
            )}

            {agent.id === orgDefaultAgentId && (
              <Badge className={styles.agentBadge} size="small" variant="info">
                {orgDefaultLabel}
              </Badge>
            )}
          </FlexBox>
        </SelectionList.Item>
      ))}
    </InputSelectionList>
  );

  return (
    <Box style={{ maxWidth: 768, minWidth: 540 }}>
      <CopilotDropdownHeader
        title="Custom Mim drafts"
        onBack={onBack}
        feedbackId=""
        additionalFeedback={additionalFeedback}
        actions={
          <Tooltip
            content={
              isDefaultAgent
                ? 'This is your default AI agent'
                : 'Save as your default AI agent'
            }
            wrap="span"
          >
            <IconButton
              icon="bookmark"
              variant="tertiary"
              disabled={!selectedAgent || isDefaultAgent}
              onClick={handleSaveDefaultAgent}
            />
          </Tooltip>
        }
      >
        {agentPicker}
      </CopilotDropdownHeader>

      <EditorCopilotDropdownContentBox isEmphasized={!!text} showUndo={false}>
        <Box minHeight="120px">{content}</Box>
      </EditorCopilotDropdownContentBox>

      <Divider />

      <CopilotDropdownFooter
        handleInsertBelow={handleInsertBelow}
        handleReplace={handleReplace}
        disableInsertBelow={!canUseDraft}
        disableReplace={!canUseDraft}
        draftContent={text}
        disableImproveReply={!canUseDraft}
      />
    </Box>
  );
});
